import { useEffect, useState } from 'react';
import type { Video } from '@/mocks/videos';

type PlayerStatus = 'idle' | 'loading' | 'error';

interface VideoPlayerProps {
  video: Video;
}

export default function VideoPlayer({ video }: VideoPlayerProps) {
  const [status, setStatus] = useState<PlayerStatus>('idle');

  useEffect(() => {
    setStatus('idle');
  }, [video.id]);

  useEffect(() => {
    if (status !== 'loading') return;
    const t = window.setTimeout(() => setStatus('error'), 2400);
    return () => window.clearTimeout(t);
  }, [status]);

  return (
    <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-100 border border-background-200">
      <img
        src={video.cover}
        alt={video.title}
        title={`${video.title} - 靠逼视频`}
        className="w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-background-950/55"></div>

      {status === 'idle' && (
        <button
          type="button"
          onClick={() => setStatus('loading')}
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 cursor-pointer group"
          aria-label="播放视频"
        >
          <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-3xl md:text-4xl transition-transform group-hover:scale-110">
            <i className="ri-play-fill"></i>
          </span>
          <span className="text-sm text-foreground-900">点击播放（演示）</span>
        </button>
      )}

      {status === 'loading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
          <span className="w-12 h-12 rounded-full border-4 border-background-300 border-t-primary-500 animate-spin"></span>
          <span className="text-sm text-foreground-800">视频加载中…</span>
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 text-3xl">
            <i className="ri-error-warning-line"></i>
          </span>
          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">播放失败</h3>
          <p className="text-xs md:text-sm text-foreground-700 max-w-md leading-relaxed">
            当前为前端播放演示，暂未接入真实视频流媒体服务，因此无法实际播放。请稍后重试或观看其他视频。
          </p>
          <button
            type="button"
            onClick={() => setStatus('loading')}
            className="mt-1 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line align-middle mr-1"></i> 重新加载
          </button>
        </div>
      )}
    </div>
  );
}